// Live-version awareness. Pagelets can be republished against the same slug (latest-wins), so the
// runtime polls the same-origin /__version endpoint while the tab is visible and shows a refresh
// toast when the page has been superseded; a toolbar button opens the version timeline (?v=N pins
// an older version, which mounts a persistent "viewing an old version" banner instead of polling).
// Everything here fails SILENT: a page saved to disk or served off-platform just never enhances.
import { ICON } from './icons'
import { setPdfState } from './toolbar'

const POLL_MS = 45_000

interface VersionInfo {
  version: number
  updatedAt: number
}
interface VersionList {
  latest: number
  versions: { version: number; createdAt: number; note: string | null; size: number }[]
}

/** view.<host>/<slug>/… shares one origin across slugs, so its endpoints are slug-prefixed;
 *  subdomain + custom-domain pages serve from the root. */
function endpointBase(): string {
  if (location.hostname.startsWith('view.')) {
    const first = location.pathname.split('/').filter(Boolean)[0]
    return first ? `/${first}` : ''
  }
  return ''
}

async function fetchJson<T>(path: string): Promise<T | null> {
  try {
    const res = await fetch(endpointBase() + path, { cache: 'no-store' })
    if (!res.ok) return null
    return (await res.json()) as T
  } catch {
    return null
  }
}

function pinnedVersion(): number | null {
  const v = new URLSearchParams(location.search).get('v')
  const n = Number(v)
  return v && Number.isInteger(n) && n >= 1 ? n : null
}

/** The current URL re-pointed at a version (null = latest, i.e. the bare canonical URL). */
function versionUrl(v: number | null): string {
  const url = new URL(location.href)
  if (v === null) url.searchParams.delete('v')
  else url.searchParams.set('v', String(v))
  return url.toString()
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]!)
}

function relTime(ts: number): string {
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000))
  if (s < 60) return 'just now'
  const m = Math.round(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.round(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.round(h / 24)
  if (d < 30) return `${d}d ago`
  return new Date(ts).toLocaleDateString()
}

export async function initVersions(): Promise<void> {
  const info = await fetchJson<VersionInfo>('/__version')
  if (!info || typeof info.version !== 'number') return // not on a pagelet origin — feature off
  void initPdfPointer()

  const pinned = pinnedVersion()
  if (pinned !== null && pinned !== info.version) {
    // A deliberately pinned old version: banner instead of polling (its content never changes).
    mountPinnedBanner(pinned, info.version)
    addTimelineButton(pinned, info.version)
    return
  }
  if (info.version > 1) addTimelineButton(info.version, info.version)
  startPolling(info.version)
}

interface PdfInfo {
  pdf: { id: string; sealUrl: string } | null
}

/** Point the toolbar's PDF button at the sealed PDF (if one exists) or the dashboard's
 *  generation modal (if not). The dashboard apex is derived from the runtime's own origin
 *  (cdn.<apex> → <apex>), so self-hosters land on their own dashboard. */
async function initPdfPointer(): Promise<void> {
  const info = await fetchJson<PdfInfo>('/__pdf')
  if (!info) return
  if (info.pdf && typeof info.pdf.sealUrl === 'string') {
    setPdfState({ kind: 'sealed', href: info.pdf.sealUrl })
    return
  }
  let apex = 'pagelet.link'
  try {
    const rt = window.__pagelet__?.base ? new URL(window.__pagelet__.base).hostname : ''
    if (rt) apex = rt.replace(/^cdn\./, '')
  } catch {
    /* keep default */
  }
  const host = location.hostname
  const parts = location.pathname.split('/').filter(Boolean)
  const slug = host.startsWith('view.') ? parts[0] : host.split('.')[0]
  if (!slug) return
  setPdfState({ kind: 'generate', href: `https://${apex}/dashboard/?pdf=1#${slug}` })
}

function startPolling(baseline: number): void {
  let latestSeen = baseline
  let dismissedFor = baseline
  let timer: number | undefined

  const tick = async (): Promise<void> => {
    if (document.visibilityState !== 'visible') return
    const info = await fetchJson<VersionInfo>('/__version')
    if (!info || typeof info.version !== 'number' || info.version <= latestSeen) return
    latestSeen = info.version
    addTimelineButton(baseline, latestSeen)
    if (latestSeen > dismissedFor) showToast(() => (dismissedFor = latestSeen))
  }
  const start = (): void => {
    if (timer === undefined) timer = window.setInterval(() => void tick(), POLL_MS)
  }
  const stop = (): void => {
    if (timer !== undefined) {
      clearInterval(timer)
      timer = undefined
    }
  }
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void tick() // catch up immediately on re-focus, then resume the cadence
      start()
    } else stop()
  })
  start()
}

function showToast(onDismiss: () => void): void {
  if (document.getElementById('pagelet-update-toast')) return
  const el = document.createElement('div')
  el.id = 'pagelet-update-toast'
  el.setAttribute('role', 'status')
  el.innerHTML = `
    <span>This page has been updated</span>
    <button data-act="refresh">Refresh</button>
    <button data-act="dismiss" aria-label="Dismiss">${ICON.close}</button>`
  el.addEventListener('click', (e) => {
    const act = (e.target as HTMLElement).closest('button')?.dataset.act
    if (act === 'refresh') {
      // Strip any ?v pin so the refresh always lands on the new latest.
      const target = versionUrl(null)
      if (target === location.href) location.reload()
      else location.href = target
    } else if (act === 'dismiss') {
      onDismiss()
      el.remove()
    }
  })
  document.body.appendChild(el)
}

/** Add the history button to the toolbar (idempotent; respects data-pagelet-toolbar="off"). */
function addTimelineButton(current: number, latest: number): void {
  if (latest < 2) return
  const bar = document.getElementById('pagelet-toolbar')
  if (!bar || bar.querySelector('[data-act="history"]')) return
  const btn = document.createElement('button')
  btn.dataset.act = 'history'
  btn.title = 'Version history'
  btn.setAttribute('aria-label', 'Version history')
  btn.innerHTML = ICON.history
  btn.addEventListener('click', () => void toggleTimeline(current))
  bar.appendChild(btn)
}

async function toggleTimeline(current: number): Promise<void> {
  const existing = document.getElementById('pagelet-versions')
  if (existing) {
    existing.remove()
    return
  }
  const list = await fetchJson<VersionList>('/__versions')
  if (!list || !Array.isArray(list.versions)) return
  const panel = document.createElement('div')
  panel.id = 'pagelet-versions'
  const items = list.versions
    .map((v) => {
      const label = `v${v.version}${v.version === list.latest ? ' · latest' : ''}`
      const meta = relTime(v.createdAt) + (v.note ? ` · ${escapeHtml(v.note)}` : '')
      return `<button data-v="${v.version}"${v.version === current ? ' class="current"' : ''}>
        <span class="pagelet-v-label">${label}</span>
        <span class="pagelet-v-meta">${meta}</span>
      </button>`
    })
    .join('')
  panel.innerHTML = `<div class="pagelet-v-title">Version history</div>${items}`
  panel.addEventListener('click', (e) => {
    const v = Number((e.target as HTMLElement).closest('button')?.dataset.v)
    if (!Number.isInteger(v) || v < 1) return
    location.href = versionUrl(v === list.latest ? null : v)
  })
  document.body.appendChild(panel)

  const close = (e: Event): void => {
    if (e instanceof KeyboardEvent && e.key !== 'Escape') return
    if (e instanceof MouseEvent && e.target instanceof Node && (panel.contains(e.target) || (e.target as HTMLElement).closest?.('#pagelet-toolbar'))) return
    panel.remove()
    document.removeEventListener('click', close, true)
    document.removeEventListener('keydown', close, true)
  }
  document.addEventListener('click', close, true)
  document.addEventListener('keydown', close, true)
}

function mountPinnedBanner(pinned: number, latest: number): void {
  if (document.getElementById('pagelet-version-banner')) return
  const el = document.createElement('div')
  el.id = 'pagelet-version-banner'
  el.innerHTML = `<span>Viewing version ${pinned} of ${latest}</span><a href="${escapeHtml(versionUrl(null))}">View latest</a>`
  document.body.appendChild(el)
  // The banner is fixed at the top — push the page down by its height so it never covers content.
  document.body.style.paddingTop = `${el.offsetHeight}px`
}
